import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utilities/axios";
import { getElements, getElementsWithSum } from "./elements";
import { getPlancompta } from "./plancompta";
import { getCategories } from "./categories";
import { getElementsOfObjective } from "./objectives";

export const categorizeImportedElementsWithAI = createAsyncThunk(
  "importedElements/categorizeImportedElementsWithAI",
  async ({ projectId, elementId }, thunkAPI) => {
    try {
      const response = await axios.post(
        `/api/elements/imported-elements/categorize`,
        { projectId: Number(projectId), elementId: Number(elementId) }
      );
      if (response.status === 200) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteImportedElement = createAsyncThunk(
  "importedElements/deleteImportedElement",
  async ({ elementId, values, projectId }, thunkAPI) => {
    try {
      const response = await axios.delete(
        `/api/elements/imported-elements/${elementId}`
      );
      if (response.status === 200) {
        thunkAPI.dispatch(getAllImportedElements(projectId));
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        } else {
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            if (values?.mode === "compta" && values?.objectiveId) {
              thunkAPI.dispatch(
                getElementsOfObjective({
                  objectiveId: values?.objective?.objectiveId,
                  isPlancompta: false,
                })
              );
            }
          });

          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        }
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// POST - Import OFX File
export const importOFXFile = createAsyncThunk(
  "importedElements/importOFXFile",
  async ({ values, formData, projectId }, thunkAPI) => {
    try {
      const response = await axios.post("/api/elements/import-ofx", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200) {
        thunkAPI.dispatch(getAllImportedElements(projectId));
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        } else {
          thunkAPI.dispatch(getCategories(values?.project)).then(() => {
            if (values?.mode === "compta" && values?.objectiveId) {
              thunkAPI.dispatch(
                getElementsOfObjective({
                  objectiveId: values?.objective?.objectiveId,
                  isPlancompta: false,
                })
              );
            }
          });

          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        }
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

// POST - Move Imported Element to Category
export const updateImportedElementLabel = createAsyncThunk(
  "importedElements/updateImportedElementLabel",
  async ({ elementId, designation, values }, thunkAPI) => {
    try {
      const response = await axios.put(
        `/api/elements/imported-elements/${elementId}`,
        { designation }
      );
      if (response.status === 200) {
        const projectId = values?.project?.projectId;
        if (projectId) {
          thunkAPI.dispatch(getAllImportedElements(projectId));
        }
        if (values?.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        } else if (values?.project) {
          if (values?.isPlancompta) {
            thunkAPI.dispatch(getPlancompta(values.project));
          } else {
            thunkAPI.dispatch(getCategories(values.project));
          }
          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        }
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const moveImportedElementToCategory = createAsyncThunk(
  "importedElements/moveImportedElementToCategory",
  async (values, thunkAPI) => {
    try {
      const response = await axios.put(
        `/api/elements/imported-elements/${values.elementId}`,
        {
          ...(values?.values?.planComptableId && {
            planComptableId: values?.values?.planComptableId,
          }),
          categoryId: values?.values?.categoryId,
          ...(values?.values?.dateComptable && {
            dateComptable: values?.values?.dateComptable,
          }),
          ...(values?.values?.designation && {
            designation: values?.values?.designation,
          }),
        }
      );
      if (response.status === 200) {
        thunkAPI.dispatch(getAllImportedElements(values?.project?.projectId));
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        } else {
          if (values?.isPlancompta) {
            thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
              if (values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: true,
                  })
                );
              }
            });
          } else {
            thunkAPI.dispatch(getCategories(values?.project)).then(() => {
              if (values?.mode === "compta" && values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: false,
                  })
                );
              }
            });
          }

          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        }
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteAllImportedElements = createAsyncThunk(
  "importedElements/deleteAllImportedElements",
  async ({ projectId, values }, thunkAPI) => {
    try {
      const response = await axios.delete(
        `/api/projects/${projectId}/imported-elements`
      );
      if (response.status === 200) {
        thunkAPI.dispatch(getAllImportedElements(values?.project?.projectId));
        if (values.isListing) {
          thunkAPI.dispatch(getElements(values?.listing));
          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        } else {
          if (values?.isPlancompta) {
            thunkAPI.dispatch(getPlancompta(values?.project)).then(() => {
              if (values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: true,
                  })
                );
              }
            });
          } else {
            thunkAPI.dispatch(getCategories(values?.project)).then(() => {
              if (values?.mode === "compta" && values?.objectiveId) {
                thunkAPI.dispatch(
                  getElementsOfObjective({
                    objectiveId: values?.objective?.objectiveId,
                    isPlancompta: false,
                  })
                );
              }
            });
          }

          thunkAPI.dispatch(getElementsWithSum(values?.sum));
        }
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getAllImportedElements = createAsyncThunk(
  "importedElements/getAllImportedElements",
  async (projectId, thunkAPI) => {
    try {
      const response = await axios.get(
        `/api/projects/${projectId}/imported-elements`
      );
      if (response.status === 200) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data || err.message);
    }
  }
);

const initialState = {
  importedElements: [],
  isLoading: false,
  importStatus: "idle", // idle, loading, succeeded, failed
  deleteStatus: "idle",
  deleteAllStatus: "idle",
  moveToeCategoryStatus: "idle",
  getAllStatus: "idle",
   categorizeStatus: "idle",
  error: null,
  importError: null,
  deleteError: null,
  deleteAllError: null,
  moveToCategoryError: null,
  getAllError: null,
  categorizeError: null,
  selectedElement: null,
  importProgress: 0,
  elementToDelete: null,
};

const importedElementsSlice = createSlice({
  name: "importedElements",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.importError = null;
      state.deleteError = null;
      state.deleteAllError = null;
      state.moveToCategoryError = null;
      state.getAllError = null;
    },
    setSelectedElement: (state, action) => {
      state.selectedElement = action.payload;
    },
    clearSelectedElement: (state) => {
      state.selectedElement = null;
    },
    setImportProgress: (state, action) => {
      state.importProgress = action.payload;
    },
    resetImportProgress: (state) => {
      state.importProgress = 0;
    },
    clearImportedElements: (state) => {
      state.importedElements = [];
    },
    setImportedElementToDelete: (state, action) => {
      state.elementToDelete = action.payload;
    },
    clearImportedElementToDelete: (state) => {
      state.elementToDelete = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Import OFX File
      .addCase(importOFXFile.pending, (state) => {
        state.importStatus = "loading";
        state.isLoading = true;
        state.importError = null;
      })
      .addCase(importOFXFile.fulfilled, (state, action) => {
        state.importStatus = "succeeded";
        state.isLoading = false;
        state.importError = null;
        // Add imported elements to the state
        if (action.payload?.elements) {
          state.importedElements = [
            ...state.importedElements,
            ...action.payload.elements,
          ];
        }
      })
      .addCase(importOFXFile.rejected, (state, action) => {
        state.importStatus = "failed";
        state.isLoading = false;
        state.importError = action.payload;
      })

      // Delete Imported Element
      .addCase(deleteImportedElement.pending, (state) => {
        state.deleteStatus = "loading";
        state.deleteError = null;
      })
      .addCase(deleteImportedElement.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.deleteError = null;
        // Remove deleted element from the state
        state.importedElements = state.importedElements.filter(
          (element) => element.id !== action.payload.elementId
        );
      })
      .addCase(deleteImportedElement.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.deleteError = action.payload;
      })

      // Move Imported Element to Category
      .addCase(moveImportedElementToCategory.pending, (state) => {
        state.moveToCategoryStatus = "loading";
        state.moveToCategoryError = null;
      })
      .addCase(moveImportedElementToCategory.fulfilled, (state, action) => {
        state.moveToCategoryStatus = "succeeded";
        state.moveToCategoryError = null;
        // Update the element in state or remove it if it's moved to category
        if (action.payload?.elementId) {
          state.importedElements = state.importedElements.filter(
            (element) => element.id !== action.payload.elementId
          );
        }
      })
      .addCase(moveImportedElementToCategory.rejected, (state, action) => {
        state.moveToCategoryStatus = "failed";
        state.moveToCategoryError = action.payload;
      })

      // Delete All Imported Elements
      .addCase(deleteAllImportedElements.pending, (state) => {
        state.deleteAllStatus = "loading";
        state.deleteAllError = null;
      })
      .addCase(deleteAllImportedElements.fulfilled, (state, action) => {
        state.deleteAllStatus = "succeeded";
        state.deleteAllError = null;
        // Clear all imported elements
        state.importedElements = [];
      })
      .addCase(deleteAllImportedElements.rejected, (state, action) => {
        state.deleteAllStatus = "failed";
        state.deleteAllError = action.payload;
      })

      // Get All Imported Elements
      .addCase(getAllImportedElements.pending, (state) => {
        state.getAllStatus = "loading";
        state.isLoading = true;
        state.getAllError = null;
      })
      .addCase(getAllImportedElements.fulfilled, (state, action) => {
        state.getAllStatus = "succeeded";
        state.isLoading = false;
        state.getAllError = null;
        // Set all imported elements
        state.importedElements =
          action.payload?.elements || action.payload || [];
      })
      .addCase(getAllImportedElements.rejected, (state, action) => {
        state.getAllStatus = "failed";
        state.isLoading = false;
        state.getAllError = action.payload;
      })
      // Categorize Imported Elements With AI
      .addCase(categorizeImportedElementsWithAI.pending, (state) => {
        state.categorizeStatus = "loading";
        state.categorizeError = null;
      })
      .addCase(categorizeImportedElementsWithAI.fulfilled, (state) => {
        state.categorizeStatus = "succeeded";
        state.categorizeError = null;
      })
      .addCase(categorizeImportedElementsWithAI.rejected, (state, action) => {
        state.categorizeStatus = "failed";
        state.categorizeError = action.payload;
      });
  },
});

export const {
  clearErrors,
  setSelectedElement,
  clearSelectedElement,
  setImportProgress,
  resetImportProgress,
  clearImportedElements,
  setImportedElementToDelete,
  clearImportedElementToDelete,
} = importedElementsSlice.actions;

export const reducer = importedElementsSlice.reducer;

export default importedElementsSlice;
